/**
 * 🏢 Corporate Booking Validation Utilities
 * Validate corporate bookings and credit limits
 *
 * ⚠️ SECURITY WARNING: These validations are for UX/UI feedback only!
 * TODO: Backend MUST implement these validations to prevent bypassing:
 * - Account status checks (only active accounts can book)
 * - Credit limit validation (prevent overdraft)
 * - Atomic balance updates (prevent race conditions)
 * - Return proper error codes (e.g., INSUFFICIENT_CREDIT, ACCOUNT_SUSPENDED)
 * Frontend validation can be bypassed via DevTools, direct API calls, etc.
 */

import { CorporateAccount } from '@/services/corporateAccountsService';

export interface CorporateBookingValidationResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
}

/**
 * Validate a corporate booking request
 */
export const validateCorporateBooking = (
  account: CorporateAccount | null,
  bookingAmount: number,
  isCorporateBooking: boolean
): CorporateBookingValidationResult => {
  const errors: string[] = [];
  const warnings: string[] = [];

  // If not a corporate booking, no validation needed
  if (!isCorporateBooking) {
    return { isValid: true, errors, warnings };
  }

  // Corporate booking must have an account
  if (!account) {
    errors.push('Corporate account is required for corporate bookings');
    return { isValid: false, errors, warnings };
  }

  // Account must be active
  // TODO: Backend must also validate this to prevent bypassing
  if (account.status !== 'active') {
    errors.push(`Corporate account is ${account.status}. Only active accounts can make bookings.`);
    return { isValid: false, errors, warnings };
  }

  // Validate booking amount
  if (bookingAmount <= 0) {
    errors.push('Booking amount must be greater than zero');
    return { isValid: false, errors, warnings };
  }

  // Check credit limit
  // TODO: Backend must also validate this to prevent bypassing via direct API calls
  if (account.availableCredit < bookingAmount) {
    errors.push(
      `Insufficient credit. Available: $${account.availableCredit.toFixed(2)}, Required: $${bookingAmount.toFixed(2)}`
    );
    return { isValid: false, errors, warnings };
  }

  // Warning if booking uses more than 90% of available credit
  const creditUtilization = bookingAmount / account.availableCredit;
  if (creditUtilization > 0.9) {
    warnings.push(
      `This booking will use ${(creditUtilization * 100).toFixed(1)}% of available credit. Remaining: $${(account.availableCredit - bookingAmount).toFixed(2)}`
    );
  }

  // Warning if account is near credit limit
  const totalUtilization = (account.currentBalance + bookingAmount) / account.creditLimit;
  if (totalUtilization > 0.9) {
    warnings.push(
      `Account will be at ${(totalUtilization * 100).toFixed(1)}% of total credit limit after this booking`
    );
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
  };
};

/**
 * Check if a corporate account has sufficient credit for a booking
 */
export const hasSufficientCredit = (
  account: CorporateAccount | null,
  bookingAmount: number
): boolean => {
  if (!account) return false;
  if (account.status !== 'active') return false;
  return account.availableCredit >= bookingAmount;
};

/**
 * Calculate remaining credit after a booking
 */
export const calculateRemainingCredit = (
  account: CorporateAccount | null,
  bookingAmount: number
): number => {
  if (!account) return 0;
  return Math.max(0, account.availableCredit - bookingAmount);
};

/**
 * Get credit utilization percentage for a booking
 */
export const getCreditUtilization = (
  account: CorporateAccount | null,
  bookingAmount: number
): number => {
  if (!account || account.availableCredit === 0) return 0;
  return (bookingAmount / account.availableCredit) * 100;
};

/**
 * Validate booking request has either customerId or corporateAccountId
 */
export const validateBookingCustomer = (
  customerId: string | undefined,
  corporateAccountId: string | undefined
): { isValid: boolean; error?: string } => {
  if (!customerId && !corporateAccountId) {
    return {
      isValid: false,
      error: 'Either customer ID or corporate account ID must be provided',
    };
  }

  if (customerId && corporateAccountId) {
    return {
      isValid: false,
      error: 'Cannot specify both customer ID and corporate account ID',
    };
  }

  return { isValid: true };
};
